/**
 * WebSocket client pro real-time komunikaci s ESP32 hydroponickým systémem
 * Poskytuje live updates senzorových dat, pump/valve statusů a system alerts
 */

import React from 'react';
import { WebSocketMessage } from '../types';
import { tokenStorage } from './token';
import { config } from '../config';

export type WebSocketEventHandler = (message: WebSocketMessage) => void;

export class HydroponicWebSocket {
  private ws: WebSocket | null = null;
  private url: string;
  private deviceId: string;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 5000;
  private pingInterval: NodeJS.Timeout | null = null;
  private eventHandlers: Map<string, WebSocketEventHandler[]> = new Map();
  // Zapamatované odběry (topiky) — po každém (re)connectu se znovu pošlou serveru,
  // protože nové spojení má na serveru prázdné subscribedDeviceIds.
  private desiredTopics: Set<string> = new Set();

  constructor(deviceId: string = '', wsUrl?: string) {
    this.deviceId = deviceId;
    this.url = wsUrl || this.buildWebSocketUrl();
  }

  private buildWebSocketUrl(): string {
    // Use dedicated WS URL if available, otherwise derive from current origin
    const wsUrlEnv = config.wsUrl;
    let wsUrl: string;

    if (wsUrlEnv) {
      wsUrl = wsUrlEnv;
    } else {
      // Use current origin for WebSocket
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}`;
    }

    // Získání JWT tokenu z localStorage nebo session storage
    const token = tokenStorage.getToken() || sessionStorage.getItem('token');

    let url = `${wsUrl}/ws`;

    // Přidání query parametrů pro autentizaci a device ID
    const params = new URLSearchParams();
    if (token) {
      params.append('token', token);
    }
    if (this.deviceId) {
      params.append('deviceId', this.deviceId);
    }

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    return url;
  }

  public connect(): Promise<void> {
    // Guard: neotvírej druhý socket, pokud už jeden běží/naskakuje.
    // (Více useWebSocket konzumentů jinak otevíralo 5 spojení naráz.)
    if (this.ws && (this.ws.readyState === WebSocket.CONNECTING || this.ws.readyState === WebSocket.OPEN)) {
      return Promise.resolve();
    }
    return new Promise((resolve, reject) => {
      try {
        // console.log(`Připojuji k WebSocket: ${this.url}`);
        this.ws = new WebSocket(this.url);

        this.ws.onopen = () => {
          // console.log(' WebSocket připojen');
          this.reconnectAttempts = 0;
          this.startPingInterval();
          this.subscribe();
          // Obnov všechny zapamatované odběry (po reconnectu má server prázdné subscriptions)
          this.desiredTopics.forEach((topic) => {
            this.send({ type: 'subscribe', topic, timestamp: new Date().toISOString() });
          });
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            this.handleMessage(message);
          } catch (error) {
            console.error('Chyba při parsování WebSocket zprávy:', error);
          }
        };

        this.ws.onclose = (event) => {
          // console.log('WebSocket odpojen:', event.code, event.reason);
          this.stopPingInterval();

          if (!event.wasClean && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket chyba:', error);
          reject(error);
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  public disconnect(): void {
    this.stopPingInterval();
    if (this.ws) {
      this.ws.close(1000, 'Manuální odpojení');
      this.ws = null;
    }
  }

  public on(eventType: string, handler: WebSocketEventHandler): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    this.eventHandlers.get(eventType)!.push(handler);
  }

  public off(eventType: string, handler: WebSocketEventHandler): void {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  public send(message: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
    // Silently drop when disconnected — reconnect logic handles recovery
  }

  // Trvalý odběr topiku: zapamatuje se a po každém (re)connectu se znovu pošle serveru.
  // Idempotentní — opakované volání se stejným topikem nic nerozbije.
  public subscribeTopic(topic: string): void {
    this.desiredTopics.add(topic);
    this.send({ type: 'subscribe', topic, timestamp: new Date().toISOString() });
  }

  public isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  private subscribe(): void {
    if (!this.deviceId) {
      // console.log('WebSocket: Žádné deviceId, přeskakuji auto-subscribe');
      return;
    }

    this.send({
      type: 'subscribe',
      topic: `device-${this.deviceId}`,
      timestamp: new Date().toISOString(),
    });

    // Subscribe to device-specific topics
    this.send({
      type: 'subscribe',
      topic: `device-${this.deviceId}/sensor_data`,
      timestamp: new Date().toISOString(),
    });

    this.send({
      type: 'subscribe',
      topic: `device-${this.deviceId}/status`,
      timestamp: new Date().toISOString(),
    });
  }

  private handleMessage(message: WebSocketMessage): void {
    // console.log('WebSocket zpráva:', message);

    const handlers = this.eventHandlers.get(message.type);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(message);
        } catch (error) {
          console.error(`Chyba v event handleru pro ${message.type}:`, error);
        }
      });
    }

    const allHandlers = this.eventHandlers.get('*');
    if (allHandlers) {
      allHandlers.forEach((handler) => {
        try {
          handler(message);
        } catch (error) {
          console.error('Chyba v univerzálním event handleru:', error);
        }
      });
    }
  }

  private scheduleReconnect(): void {
    this.reconnectAttempts++;
    // console.log(`Pokusy o reconnect: ${this.reconnectAttempts}/${this.maxReconnectAttempts}`);

    setTimeout(() => {
      // console.log('Pokus o reconnect...');
      this.connect().catch((error) => {
        console.error('Reconnect se nezdařil:', error);
      });
    }, this.reconnectInterval * this.reconnectAttempts);
  }

  private startPingInterval(): void {
    this.pingInterval = setInterval(() => {
      this.send({ type: 'ping', timestamp: new Date().toISOString() });
    }, 30000);
  }

  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }
}

export let hydroponicWebSocket: HydroponicWebSocket | null = null;

export const initializeWebSocket = (
  deviceId: string = 'ESP32-001',
  wsUrl?: string,
): HydroponicWebSocket => {
  if (hydroponicWebSocket) {
    hydroponicWebSocket.disconnect();
  }

  hydroponicWebSocket = new HydroponicWebSocket(deviceId, wsUrl);
  return hydroponicWebSocket;
};

export const useWebSocket = (deviceId: string = 'ESP32-001') => {
  const [connected, setConnected] = React.useState(false);
  const [lastMessage, setLastMessage] = React.useState<WebSocketMessage | null>(null);
  const wsRef = React.useRef<HydroponicWebSocket | null>(null);

  React.useEffect(() => {
    const ws = initializeWebSocket(deviceId);
    wsRef.current = ws;

    ws.on('*', (message) => {
      setLastMessage(message);
      if (message.type === 'pong') {
        setConnected(true);
      }
    });

    ws.connect()
      .then(() => setConnected(true))
      .catch((error) => {
        console.error('WebSocket připojení selhalo:', error);
        setConnected(false);
      });

    return () => {
      ws.disconnect();
      wsRef.current = null;
    };
  }, [deviceId]);

  const sendMessage = React.useCallback((message: any) => {
    wsRef.current?.send(message);
  }, []);

  const subscribe = React.useCallback((eventType: string, handler: WebSocketEventHandler) => {
    wsRef.current?.on(eventType, handler);

    return () => {
      wsRef.current?.off(eventType, handler);
    };
  }, []);

  return {
    connected,
    lastMessage,
    sendMessage,
    subscribe,
    webSocket: wsRef.current,
  };
};

export default HydroponicWebSocket;
